import { CHART_FONT_SIZE_CSS_VAR, CHART_FONT_SIZE_FALLBACK_PX } from '../../constants';

/**
 * Get the root font size in pixels (for rem conversion).
 * Returns 16px as the default browser root font size.
 */
function getRootFontSizePx(): number {
  if (typeof document === 'undefined') {
    return CHART_FONT_SIZE_FALLBACK_PX;
  }

  const rootFontSize = getComputedStyle(document.documentElement).fontSize;
  const parsed = parseFloat(rootFontSize);
  return Number.isNaN(parsed) || parsed <= 0 ? CHART_FONT_SIZE_FALLBACK_PX : parsed;
}

/**
 * Get the chart font size in pixels from computed CSS.
 * Falls back to 14px if the CSS variable is not available (e.g., SSR)
 * or returns an invalid value (0 or very small).
 *
 * Handles both px and rem values:
 * - "14px" -> 14
 * - "0.875rem" -> 14 (assuming 16px root font size)
 */
export function getChartFontSize(): number {
  if (typeof document === 'undefined') {
    return CHART_FONT_SIZE_FALLBACK_PX;
  }

  const fontSizeValue = getComputedStyle(document.documentElement).getPropertyValue(CHART_FONT_SIZE_CSS_VAR).trim();

  if (!fontSizeValue) {
    return CHART_FONT_SIZE_FALLBACK_PX;
  }

  const parsed = parseFloat(fontSizeValue);

  // Fall back if NaN or too small
  if (Number.isNaN(parsed) || parsed <= 0) {
    return CHART_FONT_SIZE_FALLBACK_PX;
  }

  // Convert rem to px if the value is in rem
  if (fontSizeValue.endsWith('rem')) {
    const rootFontSize = getRootFontSizePx();
    const pxValue = parsed * rootFontSize;
    return pxValue > 0 ? pxValue : CHART_FONT_SIZE_FALLBACK_PX;
  }

  // For px values or unitless numbers, use as-is
  // But ensure it's a reasonable size (at least 8px)
  return parsed >= 8 ? parsed : CHART_FONT_SIZE_FALLBACK_PX;
}
